import Box from '@mui/material/Box';
import Menu from '@mui/material/Menu';
import { ELEVATION } from '@openx/components-core/lib/theme/elevation.ts';
import { theme } from '@openx/components-core/lib/theme/theme.ts';
import type { PackageFormState } from '@openx/types/package.ts';
import { CaretDownIcon } from '@phosphor-icons/react/CaretDown';
import { CaretUpIcon } from '@phosphor-icons/react/CaretUp';
import { useCallback, useMemo, useState } from 'react';
import { type Control, useFormContext, useFormState } from 'react-hook-form';
import type { RequiredTargetingSectionKeysType } from '../context/types.ts';
import type { TargetingPresetOptionProps } from '../utils/types.ts';
import { useSubOptionCheckbox } from '../utils/useSubOptionCheckbox.ts';
import { TargetingPresetOption } from './TargetingPresetOption.tsx';

export type TargetingPresetElementProps = {
  mainOption: TargetingPresetOptionProps;
  subOptions?: TargetingPresetOptionProps[];
};

type TargetingPresetElementFullProps = TargetingPresetElementProps & {
  sectionKey: RequiredTargetingSectionKeysType;
  elementIndex: number;
};

export const TargetingPresetElement = (props: TargetingPresetElementFullProps) => {
  const formMethods = useFormContext<PackageFormState>();

  return formMethods ? (
    <TargetingPresetElementWithFormState {...props} control={formMethods.control} />
  ) : (
    <TargetingPresetElementView {...props} hasError={false} />
  );
};

// Subscribes to form state via useFormState so `hasError` re-renders when validation
// resolves asynchronously — calling getFieldState() imperatively during render doesn't.
const TargetingPresetElementWithFormState = ({
  control,
  ...props
}: TargetingPresetElementFullProps & { control: Control<PackageFormState> }) => {
  const { errors } = useFormState({ control });

  const hasError = !!(
    (props.sectionKey === 'channel' && errors?.targeting?.rendering_context?.distribution_channel) ||
    (props.sectionKey === 'format' && errors?.targeting?.rendering_context?.ad_placement) ||
    (props.sectionKey === 'device' && errors?.targeting?.rendering_context?.device_type)
  );

  return <TargetingPresetElementView {...props} hasError={hasError} />;
};

const TargetingPresetElementView = ({
  mainOption,
  subOptions = [],
  sectionKey,
  elementIndex,
  hasError,
}: TargetingPresetElementFullProps & { hasError: boolean }) => {
  const [anchorElement, setAnchorElement] = useState<null | HTMLElement>(null);
  const hasSubOptions = subOptions.length > 0;
  const selectedSubOptionsCount = useMemo(
    () => subOptions.filter(subOption => subOption.isSelected).length,
    [subOptions]
  );

  const { customCheckbox } = useSubOptionCheckbox(subOptions, sectionKey, elementIndex);

  const { key: mainOptionKey, label: mainOptionLabel, icon, isSelected, isDisabled, isError, onClick } = mainOption;

  const borderColor = isError ? theme.palette.error.dark : theme.palette.primary.light;

  const handleMainClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      if (hasSubOptions) {
        setAnchorElement(event.currentTarget);
      } else {
        try {
          onClick?.(event, sectionKey, elementIndex);
        } catch (error) {
          throw new Error(error.message);
        }
      }
    },
    [onClick, sectionKey, elementIndex, hasSubOptions]
  );

  const handleClose = useCallback(() => setAnchorElement(null), []);

  const labelWithArrow = (
    <Box sx={theme => ({ display: 'flex', alignItems: 'center', gap: theme.spacing(0.5) })}>
      {`${mainOption.label} (${selectedSubOptionsCount})`}
      {hasSubOptions && (anchorElement ? <CaretUpIcon size={20} /> : <CaretDownIcon size={20} />)}
    </Box>
  );

  return (
    <Box data-test="deal-preset-element" sx={{ height: 44 }}>
      <TargetingPresetOption
        key={mainOptionKey}
        label={hasSubOptions ? labelWithArrow : mainOptionLabel}
        icon={icon}
        isSelected={isSelected}
        isDisabled={isDisabled}
        isError={hasError}
        onClick={handleMainClick}
        isDropdownOpen={Boolean(anchorElement)}
        customCheckbox={hasSubOptions ? customCheckbox : undefined}
      />
      {hasSubOptions && (
        <Menu
          open={Boolean(anchorElement)}
          anchorEl={anchorElement}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          disableAutoFocus
          disableEnforceFocus
          disableScrollLock
          slotProps={{
            paper: {
              sx: {
                minWidth: anchorElement?.offsetWidth || undefined,
                borderRadius: '0 0 8px 8px',
                p: 0,
                boxShadow: ELEVATION.elevation8,
                border: `1px solid ${borderColor}`,
                backgroundColor: hasError ? theme.palette.error.light : theme.palette.common.white,
                display: 'flex',
                flexDirection: 'column',
                marginTop: '-1px',
                borderTop: 'none',
              },
            },

            list: { dense: true, disablePadding: true },
          }}
          data-test="sub-option-menu"
        >
          {subOptions.map((subOption, index) => (
            <TargetingPresetOption
              key={subOption.key}
              label={subOption.label}
              icon={subOption.icon}
              isSelected={subOption.isSelected}
              isDisabled={subOption.isDisabled}
              isError={Boolean(subOption.isError ?? mainOption.isError)}
              onClick={event => subOption.onClick?.(event, sectionKey, elementIndex, index)}
              isSubOption
            />
          ))}
        </Menu>
      )}
    </Box>
  );
};
